import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { Loader } from '@hologram-dimension/loader';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { useVirtualizer } from '@tanstack/react-virtual';
import useOpenSessions from 'api/legacy/session/useOpenSessions';
import useSims from 'api/sims/useSims';
import useSimsCount from 'api/sims/useSimsCount';
import useSimsIds from 'api/sims/useSimsIds';
import { clsx } from 'clsx/lite';
import useSimInventoryFilters from 'hooks/SimInventory/useSimInventoryFilters';
import useSimInventorySorts from 'hooks/SimInventory/useSimInventorySorts';
import useUserInfo from 'hooks/User/useUserInfo';
import {
  type ReactNode,
  type UIEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Sort } from 'state/SimInventory/SimInventoryStore';
import useSimInventorySelected from 'state/SimInventory/useSimInventorySelected';
import useSimInventorySelectionStore from 'state/SimInventory/useSimInventorySelectionStore';
import useSimInventoryStore from 'state/SimInventory/useSimInventoryStore';
import type { SimsDaoSim } from 'types/__generated__/SimInventoryApi';
import type {
  ActivateButtonRenderer,
  OnSimsModified,
  SimInventorySegmentName,
  SimKey,
} from 'types/common';
import useDebounceCallback from 'utils/hooks/useDebounceCallback';
import useColumnsForSegment from './ColumnDefinition/useColumnDefsForSegment';
import styles from './SimInventoryTable.module.css';
import SimInventoryTableRow from './SimInventoryTableRow';
import {
  COL_OVERSCAN,
  type MaxWidths,
  type MeasureElement,
  OnCheckboxToggled,
  PAGE_SIZE,
  ROW_HEIGHT,
  ROW_OVERSCAN,
  SimCellRendererProps,
  TABLE_CELL_PADDING,
} from './SimInventoryTableShared';

interface SimInventoryTableProps {
  segmentName: SimInventorySegmentName;
  className?: string;
  simDetailBaseUrl: string;
  handleLinkClick: (url: string, value: any) => void;
  onValidatingChange: (validating: boolean) => void;
  activateButtonRenderer?: ActivateButtonRenderer;
}

const SimInventoryTable = forwardRef<OnSimsModified, SimInventoryTableProps>(
  (
    {
      segmentName,
      className,
      simDetailBaseUrl,
      handleLinkClick,
      onValidatingChange,
      activateButtonRenderer,
    },
    ref
  ) => {
    const [pageToLoad, setPageToLoad] = useState<number>(0);

    const {
      filters,
      hasFilters,
      hasUserFilterApplied,
      filtersValidForAllSegment,
      error: filtersError,
      isLoading: isLoadingFilters,
    } = useSimInventoryFilters({
      segmentName,
    });

    const { resetFilters } = useSimInventoryStore((state) => state);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);

    const { columnDefs, columnsAsRow, defaultSort } = useColumnsForSegment({ segmentName });
    const [sort, setSort] = useState<Array<Sort>>(defaultSort);

    const nextSort = useSimInventorySorts({ defaultSort });

    const updateSort = useCallback(() => {
      setSort(nextSort);
    }, [nextSort]);

    // 0 for now, feels off if there's a lag
    useDebounceCallback(updateSort, 0);

    const { isSuperAdmin } = useUserInfo();

    const {
      error: openSessionsError,
      isLoading: openSessionsLoading,
      isValidating: openSessionsValidating,
      mutate: mutateOpenSessions,
    } = useOpenSessions(
      filters.organizationId,
      (isSuperAdmin && hasFilters) || !!filters.organizationId
    );

    const canLoadSimsCount =
      hasFilters &&
      (segmentName !== 'all' || filtersValidForAllSegment) &&
      !filtersError &&
      !isLoadingFilters;

    const {
      count: max,
      error: simsCountError,
      isLoading: countLoading,
      mutate: mutateSimsCount,
      isValidating: validatingCount,
    } = useSimsCount(filters, canLoadSimsCount);

    const canLoadSims = canLoadSimsCount && max > 0 && !countLoading && !simsCountError;

    const rowVirtualizer = useVirtualizer({
      count: max,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => ROW_HEIGHT,
      overscan: ROW_OVERSCAN,
    });

    const getDefaultMaxWidths = useCallback(
      () =>
        [...columnDefs.values()].reduce<MaxWidths>((acc, column) => {
          acc[column.value] = {
            width:
              (column.value as any) === 'checkbox'
                ? column.width
                : column.width + TABLE_CELL_PADDING,
            measured: false,
          };

          return acc;
        }, {} as MaxWidths),
      [columnDefs]
    );

    // TODO: Consider saving this in persistent state for later use cross-session. Or leverage the column virtualizer to do so
    const [maxWidths, setMaxWidths] = useState<MaxWidths | null>(getDefaultMaxWidths());

    const getColumnWidth = useCallback(
      (value: SimKey) => (maxWidths && maxWidths[value]?.width) ?? 0,
      [maxWidths]
    );
    const getColumnWidthByIdx = useCallback(
      (index: number) => {
        const colDefArr = [...columnDefs.values()];
        const colDefValue = colDefArr[index].value;
        const curMaxWidth =
          maxWidths && colDefValue in maxWidths
            ? maxWidths[colDefValue].width
            : colDefArr[index].width;

        return curMaxWidth;
      },
      [columnDefs, maxWidths]
    );

    const measureElement: MeasureElement = useCallback((el, value) => {
      const elWidth = Math.ceil(el?.getBoundingClientRect().width ?? 0);

      if (!elWidth || elWidth === 0 || value === 'checkbox') {
        return;
      }

      setMaxWidths((lastMaxWidths) => {
        const maxWidth = lastMaxWidths && lastMaxWidths[value];

        if (!maxWidth) {
          // Defaults haven't been set, do not do premature calculations
          return lastMaxWidths;
        }

        const newWidth = maxWidth.measured
          ? Math.max(elWidth + TABLE_CELL_PADDING, maxWidth.width)
          : elWidth + TABLE_CELL_PADDING;

        if (newWidth === lastMaxWidths[value]?.width) {
          return lastMaxWidths;
        }

        const newMaxWidths = structuredClone(lastMaxWidths);

        newMaxWidths[value] = { width: newWidth, measured: true };

        return newMaxWidths;
      });
    }, []);

    const columnVirtualizer = useVirtualizer({
      horizontal: true,
      count: columnDefs.size,
      getScrollElement: () => scrollRef.current,
      estimateSize: getColumnWidthByIdx,
      overscan: COL_OVERSCAN,
    });

    const columnItems = columnVirtualizer.getVirtualItems();
    const currentPage = Math.floor((rowVirtualizer.range?.startIndex ?? 0) / PAGE_SIZE);

    const updatePageToLoad = useCallback(() => {
      setPageToLoad(currentPage);
    }, [currentPage]);

    useDebounceCallback(updatePageToLoad, 500);

    // Load SIMs
    // TODO: Optimization opportunity: we don't need pages anymore, so consider improving these loading algorithms with velocity and offsets from scroll position rather than page size
    const currentOffset = pageToLoad * PAGE_SIZE;
    const {
      sims: currentSims,
      error: currentSimsError,
      mutate: mutateCurrentSims,
      isValidating: validatingCurrent,
      isLoading: loadingCur,
    } = useSims({
      filters: {
        ...filters,
        pagination: { offset: currentOffset, limit: PAGE_SIZE },
      },
      sort,
      canLoad: segmentName !== 'all' || filtersValidForAllSegment, // Always load the current page, except all which should wait for one of the min required filters
    });

    const {
      sims: nextPageSims,
      error: nextPageSimsError,
      mutate: mutateNextSims,
      isValidating: validatingNext,
      isLoading: loadingNext,
    } = useSims({
      filters: {
        ...filters,
        pagination: {
          offset: max === -1 || max < PAGE_SIZE ? -1 : currentOffset + PAGE_SIZE,
          limit: PAGE_SIZE,
        },
      },
      sort,
      canLoad: canLoadSims,
    });

    const {
      sims: prevPageSims,
      error: prevPageSimsError,
      mutate: mutatePrevSims,
      isValidating: validatingPrev,
      isLoading: loadingPrev,
    } = useSims({
      filters: {
        ...filters,
        pagination: { offset: max === -1 ? -1 : currentOffset - PAGE_SIZE, limit: PAGE_SIZE },
      },
      sort,
      canLoad: canLoadSims,
    });

    // Translate loaded SIMs to virtualized SIMs
    const { sims, blockRender } = useMemo<{
      sims: Array<SimsDaoSim | undefined>;
      blockRender: boolean;
    }>(() => {
      if (pageToLoad === currentPage) {
        return { sims: [...prevPageSims, ...currentSims, ...nextPageSims], blockRender: false };
      }
      if (pageToLoad - 1 === currentPage) {
        // Scrolling up
        return {
          sims: [...new Array(PAGE_SIZE), ...prevPageSims, ...currentSims],
          blockRender: false,
        };
      }
      if (pageToLoad + 1 === currentPage) {
        // Scrolling down
        return {
          sims: [...currentSims, ...nextPageSims, ...new Array(PAGE_SIZE)],
          blockRender: false,
        };
      }

      if (pageToLoad - 2 === currentPage) {
        // Scrolling down pretty fast
        return { sims: [...nextPageSims, ...new Array(PAGE_SIZE * 2)], blockRender: false };
      }

      if (pageToLoad + 2 === currentPage) {
        // Scrolling up pretty fast
        return { sims: [...new Array(PAGE_SIZE * 2), ...prevPageSims], blockRender: false };
      }

      return { sims: new Array(PAGE_SIZE * 3), blockRender: true };
    }, [currentPage, currentSims, nextPageSims, pageToLoad, prevPageSims]);

    // TODO: Consider also splitting to own hook. Or perhaps re-adding to SimInventoryTableCheckbox. In that case we would have to pass down org ID and segment name and handle loading filters/etc

    const error: unknown | undefined =
      simsCountError ||
      currentSimsError ||
      nextPageSimsError ||
      prevPageSimsError ||
      filtersError ||
      openSessionsError;

    let tableContents: ReactNode = null;
    let emptyState: ReactNode = null;

    const customRendererProps: SimCellRendererProps = useMemo(
      () => ({
        simDetailBaseUrl,
        handleLinkClick,
        segmentName,
      }),
      [handleLinkClick, segmentName, simDetailBaseUrl]
    );

    const scrollHandler = useCallback<UIEventHandler<HTMLDivElement>>(() => {
      if (headerRef.current && scrollRef.current) {
        headerRef.current.style.transform = `translateX(-${scrollRef.current.scrollLeft}px)`;
      }
    }, []);

    // TODO: Consider wrapping these in a hook
    const { sims: allSelectedSims } = useSimInventorySelected(segmentName);
    const { addSelectedSim, removeSelectedSim, setSelectedSims, resetSelection } =
      useSimInventorySelectionStore();

    const {
      simIds: allFilteredSimIds,
      isLoading: allSimIdsLoading,
      error: allSimIdsError,
      mutate: mutateSimIds,
      isValidating: isValidatingSimIds,
    } = useSimsIds({ filters, canLoad: canLoadSimsCount });

    const onAllCheckboxToggled = useCallback(
      (checked: boolean) => {
        if (checked) {
          const newIds = new Map(allFilteredSimIds);
          if (allSelectedSims) {
            allSelectedSims.forEach((value, key) => {
              newIds.set(key, value);
            });
          }

          setSelectedSims(segmentName, newIds, 'checked');
        } else {
          resetSelection(segmentName);
        }
      },
      [allFilteredSimIds, allSelectedSims, resetSelection, segmentName, setSelectedSims]
    );

    const onCheckboxToggled = useCallback<OnCheckboxToggled>(
      (checked, hologramSimId) => {
        if (!hologramSimId) {
          return;
        }

        if (checked) {
          addSelectedSim(segmentName, hologramSimId);
        } else {
          removeSelectedSim(segmentName, hologramSimId);
        }
      },
      [addSelectedSim, removeSelectedSim, segmentName]
    );

    const allowToggle =
      canLoadSimsCount && !allSimIdsLoading && !allSimIdsError && !isValidatingSimIds;

    // Allow wrappers to refresh us

    // TODO: Consider that perhaps so much is duplicated between various components, and the spiderweb of calls and callbacks is getting gnarly, maybe foist all loading up to SimInventory
    //       Or consider how, similar to SIM details, all loading should be triggered in one place and then components can still use it. This way we can refresh in one place

    const refresh = useCallback(() => {
      mutateSimsCount();
      mutateCurrentSims();
      mutateNextSims();
      mutatePrevSims();
      mutateSimIds();
      mutateOpenSessions();
      onValidatingChange(true);
    }, [
      mutateCurrentSims,
      mutateNextSims,
      mutateOpenSessions,
      mutatePrevSims,
      mutateSimIds,
      mutateSimsCount,
      onValidatingChange,
    ]);

    const isValidating =
      validatingCount ||
      validatingNext ||
      validatingPrev ||
      validatingCurrent ||
      openSessionsValidating;
    const isLoading = countLoading || isLoadingFilters || blockRender;

    useEffect(() => {
      onValidatingChange(
        !(isLoading || loadingCur || loadingNext || loadingPrev || openSessionsLoading) &&
          isValidating
      );
    }, [
      isLoading,
      isValidating,
      loadingCur,
      loadingNext,
      loadingPrev,
      onValidatingChange,
      openSessionsLoading,
    ]);

    useImperativeHandle(ref, () => refresh, [refresh]);

    // TODO: API is moving away from 404's, so should we
    // TODO: Consider whether memoization helps stabilize/increase performance here. We should be flowing naturally from state to state, and not returning often. But worth looking into
    if (
      (!countLoading && max === 0 && hasFilters && hasUserFilterApplied) ||
      simsCountError?.status === 404 ||
      filtersError?.status === 404
    ) {
      emptyState = (
        <PanelNotification
          className={styles.emptyWrapper}
          icon={<Icon name="Search" size="large" fill="DdsColorTextDisabledOndefault" />}
          actions={
            <Button variant="secondary" onClick={() => resetFilters()}>
              Reset filters
            </Button>
          }
        >
          No SIMs match the selected filters.
        </PanelNotification>
      );
    } else if (segmentName === 'all' && !filtersValidForAllSegment) {
      emptyState = (
        <PanelNotification icon="Search" className={styles.emptyWrapper}>
          Enter an organization or ICCID to find SIMs
        </PanelNotification>
      );
    }
    // None found
    else if (!countLoading && max === 0) {
      if (segmentName === 'deactivated') {
        emptyState = (
          <PanelNotification
            className={styles.emptyWrapper}
            label="No deactivated SIMs found"
            icon={
              <Icon name="StatusDeactivated" size="large" fill="DdsColorTextDisabledOndefault" />
            }
          >
            Deactivated SIMs will be listed here. These SIMs are no longer able to use data and
            cannot be reactivated.
          </PanelNotification>
        );
      } else if (segmentName === 'preflight') {
        emptyState = (
          <PanelNotification
            className={styles.emptyWrapper}
            label="Order SIMs to get started"
            icon={<Icon name="StatusTesting" size="large" fill="DdsColorTextDisabledOndefault" />}
            actions={
              <Button
                type="button"
                variant="secondary"
                iconEnd="ExternalLink"
                href="https://www.hologram.io/contact-sales/"
                target="_blank"
              >
                Contact the Sales Team
              </Button>
            }
          >
            After you have received SIMs set up with pre-deployment testing, they will show up here.
          </PanelNotification>
        );
      } else if (segmentName === 'active') {
        emptyState = (
          <PanelNotification
            className={styles.emptyWrapper}
            label="Activate SIMs to get started"
            icon={
              <Icon name="StatusActivating" size="large" fill="DdsColorTextDisabledOndefault" />
            }
            actions={activateButtonRenderer?.({
              children: 'Activate your first SIMs',
              variant: 'primary',
            })}
          >
            Your SIMs will show up here after activation.
          </PanelNotification>
        );
      }
    }
    // Note: we specifically do not call endpoints that cannot return results (next page if beyond the max, prev page if at the start)
    else if (error) {
      emptyState = (
        <PanelNotification
          className={styles.emptyWrapper}
          variant="error"
          label="We ran into an issue loading your SIMs"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          You can try to change your filters, or reload the page.
        </PanelNotification>
      );
    } else if (!hasFilters) {
      emptyState = (
        <PanelNotification
          className={styles.emptyWrapper}
          icon={<Icon name="Search" size="large" />}
        >
          Set filters to find SIMs
        </PanelNotification>
      );
    } else if (isLoading) {
      // TOOD: Reconsider this from a UX perspective
      emptyState = (
        <div className={styles.emptyWrapper}>
          <Loader>Loading matching SIMs...</Loader>
        </div>
      );
    } else {
      tableContents =
        sims &&
        rowVirtualizer
          .getVirtualItems()
          .map((virtualRow) => (
            <SimInventoryTableRow
              key={`${virtualRow.key}`}
              sim={sims[virtualRow.index + -(currentPage - 1) * PAGE_SIZE]}
              segmentName={segmentName}
              virtualRow={virtualRow}
              columnVirtualizer={columnVirtualizer}
              columnItems={columnItems}
              columnDefs={columnDefs}
              getColumnWidth={getColumnWidth}
              measureElement={measureElement}
              customRendererProps={customRendererProps}
              allowToggle={allowToggle}
              onCheckboxToggled={onCheckboxToggled}
              onAllCheckboxToggled={onAllCheckboxToggled}
            />
          ));
    }

    return (
      <div className={clsx(styles.simInventory, className)}>
        <SimInventoryTableRow
          key="header-row"
          sim={columnsAsRow}
          segmentName={segmentName}
          virtualRow={{ start: 0, size: 33, index: 0, end: 33, key: '', lane: 0 }}
          columnVirtualizer={columnVirtualizer}
          columnItems={columnItems}
          columnDefs={columnDefs}
          getColumnWidth={getColumnWidth}
          measureElement={measureElement}
          isHeader
          ref={headerRef}
          customRendererProps={customRendererProps}
          allowToggle={allowToggle}
          onCheckboxToggled={onCheckboxToggled}
          onAllCheckboxToggled={onAllCheckboxToggled}
        />
        {emptyState}
        <div ref={scrollRef} className={styles.scrollWrapper} onScroll={scrollHandler}>
          <div
            className={styles.scroller}
            style={{
              minWidth: `${columnVirtualizer.getTotalSize()}px`,
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {tableContents}
          </div>
        </div>
      </div>
    );
  }
);

export default SimInventoryTable;
